import { useState } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

interface AddCardFormProps {
  onSuccess: () => void;
  onCancel: () => void;
}

const CARD_ELEMENT_OPTIONS = {
  hidePostalCode: true, // Désactivé car codes postaux non utilisés en RDC
  style: {
    base: {
      color: '#32325d',
      fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: '#aab7c4'
      }
    },
    invalid: {
      color: '#fa755a',
      iconColor: '#fa755a'
    }
  }
};

export const AddCardForm = ({ onSuccess, onCancel }: AddCardFormProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements || !user) {
      return;
    }

    setLoading(true);

    try {
      // Create SetupIntent
      const { data: setupData, error: setupError } = await supabase.functions.invoke(
        'create-stripe-setup-intent'
      );

      if (setupError) throw setupError;

      const cardElement = elements.getElement(CardElement);
      if (!cardElement) throw new Error('Card element not found');

      // Confirm card setup
      const { setupIntent, error: stripeError } = await stripe.confirmCardSetup(
        setupData.clientSecret,
        {
          payment_method: {
            card: cardElement,
          }
        }
      );

      if (stripeError) throw stripeError;

      if (setupIntent?.payment_method) {
        // The payment method ID is returned, but we need to get details from the server
        const { data: paymentMethodData, error: pmError } = await supabase.functions.invoke(
          'get-payment-method-details',
          { body: { paymentMethodId: setupIntent.payment_method } }
        );

        if (pmError) throw pmError;

        // Save to database
        const { error: dbError } = await supabase.from('payment_methods').insert({
          user_id: user.id,
          type: 'card',
          card_brand: paymentMethodData.card.brand,
          last_four: paymentMethodData.card.last4,
          stripe_payment_method_id: setupIntent.payment_method as string,
          is_default: false
        });

        if (dbError) throw dbError;

        toast.success('Carte ajoutée avec succès');
        onSuccess();
      }
    } catch (error: any) {
      console.error('Error adding card:', error);
      toast.error(error.message || 'Erreur lors de l\'ajout de la carte');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <label className="text-sm font-medium">Informations de carte</label>
        <div className="border rounded-md p-3">
          <CardElement options={CARD_ELEMENT_OPTIONS} />
        </div>
      </div>
      <div className="flex space-x-2">
        <Button
          type="button"
          variant="outline"
          className="flex-1"
          onClick={onCancel}
          disabled={loading}
        >
          Annuler
        </Button>
        <Button
          type="submit"
          className="flex-1"
          disabled={!stripe || loading}
        >
          {loading ? 'Enregistrement...' : 'Enregistrer la carte'}
        </Button>
      </div>
    </form>
  );
};
